import React, { useEffect, useState } from "react";
import { DEFAULT_TIMINGS, setTimings } from "../../config";
import Axios from "../../state/utils/api-caller";
import { Spin } from "../loading";
import { Config, configContext, DEFAULT_CLIENT_ID } from "./context";

const { Provider } = configContext;

/**
 * `dataStartMonth` is fed straight to `<input type="month" min>` and to the
 * overview API, so a typo must not reach either. Same principle as the timing
 * guards in config/timings.ts: a bad value falls back to "no bound", which is
 * how these screens behaved before the key existed.
 */
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

interface ConfigProviderProps {
  children: any;
}

export function ConfigProvider(props: ConfigProviderProps) {
  const { children } = props;
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<Config>({
    arcgisURL: "",
    baseURL: "",
    authServer: "",
    clientId: DEFAULT_CLIENT_ID,
    disableBollard: false,
    hkMapKey: "",
    dataStartMonth: undefined,
    ...DEFAULT_TIMINGS,
  });

  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((loadedConfig) => {
        const next = loadedConfig as Partial<Config>;
        Axios.defaults.baseURL = next.baseURL;
        // Publish the timings to the non-React modules (activity tracking, the
        // token helpers, the Axios interceptor) BEFORE any child renders, so
        // nothing reads a default it should not have.
        setTimings(next);
        setConfig({
          ...DEFAULT_TIMINGS,
          ...(next as Config),
          // Tolerate a config.json written before clientId existed.
          clientId: next.clientId || DEFAULT_CLIENT_ID,
          dataStartMonth: MONTH.test(next.dataStartMonth ?? "")
            ? next.dataStartMonth
            : undefined,
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <Provider value={config}>
      {loaded ? error ? <div>{error}</div> : children : <Spin />}
    </Provider>
  );
}
